import { fmtZahl, jahrLabel, normiert, uid } from "./format.js";
import { TYPEN } from "./konstanten.js";


/* Anzeigetitel eines Jahrgangs innerhalb seines Weins. Gibt es mehrere
   Jahrgänge mit gleichem Label (v. a. mehrere N.V.), wird durchnummeriert. */
function jahrgangTitel(wein, jg) {
  const basis = jahrLabel(jg.jahr);
  if (!wein) return basis;
  const gleiche = wein.jahrgaenge.filter((j) => jahrLabel(j.jahr) === basis);
  if (gleiche.length <= 1) return basis;
  const idx = gleiche.findIndex((j) => j.id === jg.id);
  return `${basis} (${idx + 1})`;
}


function winzerZuWein(db, wein) {
  return db.winzer.find((w) => w.id === wein.winzerId) || null;
}


function regionZuWinzer(db, winzer) {
  return winzer ? db.regions.find((r) => r.id === winzer.regionId) || null : null;
}


/* Region eines Weins: bevorzugt die am Wein gespeicherte, sonst die
   des Winzers. So bleibt die Region auch bei abweichender Zuordnung
   (z. B. Zukäufe, Verschnitte über Grenzen) korrekt. */
function regionZuWein(db, wein) {
  if (wein.regionId) {
    const r = db.regions.find((x) => x.id === wein.regionId);
    if (r) return r;
  }
  return regionZuWinzer(db, winzerZuWein(db, wein));
}


/* Kritiken eines Weins.

   Die erste Bedingung ist keine Formsache: Noch nicht zugeordnete
   Kritiken tragen `weinId: null` (siehe kern/kritik.js). Ohne den
   Riegel würde ein von außen durchgereichtes `weinId == null` — etwa
   aus einem Wein, den es nicht mehr gibt — genau sie zutage fördern
   und in einer Weinansicht anzeigen, in die sie nicht gehören. */
function kritikenFuer(db, weinId, jahr = null, jgId = null) {
  if (!weinId) return [];
  return db.kritiken.filter((k) => k.weinId === weinId
    && (jgId != null ? k.jgId === jgId : (jahr == null || k.jahr === jahr)));
}


const gesamtFlaschen = (jg) => Object.values(jg.bestaende || {}).reduce((s, n) => s + (n || 0), 0);

const flaschenVonWein = (wein) => wein.jahrgaenge.reduce((s, j) => s + gesamtFlaschen(j), 0);

const flaschenVonWinzer = (db, winzerId) =>
  db.weine.filter((w) => w.winzerId === winzerId).reduce((s, w) => s + flaschenVonWein(w), 0);

const flaschenInRegion = (db, regionId) =>
  db.winzer.filter((w) => w.regionId === regionId).reduce((s, w) => s + flaschenVonWinzer(db, w.id), 0);


/* Anzahl Jahrgänge eines Weins — optional nur solche mit Bestand. */
const jahrgaengeZahl = (wein, nurBestand) =>
  nurBestand ? wein.jahrgaenge.filter((j) => gesamtFlaschen(j) > 0).length : wein.jahrgaenge.length;


/* Anzahl Weine eines Winzers — optional nur solche mit Bestand. */
const weineZahlVonWinzer = (db, winzerId, nurBestand) =>
  db.weine.filter((w) => w.winzerId === winzerId && (!nurBestand || flaschenVonWein(w) > 0)).length;


/* Wert der eingelagerten Flaschen eines Jahrgangs. Der Preis gilt je
   Flasche der jeweiligen Größe (so wird er auch erfasst), daher OHNE
   Volumengewichtung — gleiche Lesart wie in der Statistik. */
function jahrgangsWert(jg, karte) {
  const preis = flaschenPreis(jg, karte);
  if (preis == null) return 0;
  return preis * gesamtFlaschen(jg);
}


function rebsortenListe(weine) {
  const menge = new Set();
  for (const w of weine) {
    for (const r of rebsortenListe2(w)) menge.add(r.name);
  }
  return [...menge].sort((a, b) => a.localeCompare(b, "de"));
}


function hatRebsorte(wein, sorte) {
  return rebsortenListe2(wein).some((r) => r.name.toLowerCase() === sorte.toLowerCase());
}


/* Strukturierte Rebsortenliste eines Weins: [{ name, anteil|null }].
   Fällt auf das alte Textfeld zurück (Namen ohne Anteil). */
/* Löst einen im Namen steckenden Anteil heraus — „Chardonnay (50%)"
   oder „Chardonnay 50 %" wird zu { name: "Chardonnay", anteil: 50 }.
   Nötig für Altbestände, deren Sortennamen die Prozentangabe tragen;
   sonst gälten „Chardonnay" und „Chardonnay (50%)" als zwei Sorten. */
function trenneAnteil(name, anteil) {
  const roh = String(name || "").trim();
  const m = roh.match(/^(.*?)[\s]*\(?\s*(\d+(?:[.,]\d+)?)\s*%\s*\)?$/);
  if (m && m[1].trim()) {
    return { name: m[1].trim(), anteil: anteil != null ? anteil : parseFloat(m[2].replace(",", ".")) };
  }
  return { name: roh, anteil: anteil != null ? anteil : null };
}

function rebsortenListe2(wein) {
  if (Array.isArray(wein.rebsortenListe)) {
    return wein.rebsortenListe
      .map((r) => trenneAnteil(r.name, r.anteil != null ? r.anteil : null))
      .filter((r) => r.name);
  }
  return String(wein.rebsorten || "").split(",")
    .map((t) => t.trim()).filter(Boolean)
    .map((t) => trenneAnteil(t, null));
}


/* Bewegungen (Zugänge/Entnahmen) eines Weins, neueste zuerst. */
function bewegungenVonWein(db, weinId) {
  return (db.bewegungen || [])
    .filter((b) => b.weinId === weinId)
    .slice()
    .sort((a, b) => (b.datum || "").localeCompare(a.datum || ""));
}


/* Zeitpunkt der letzten Einlagerung (jüngster Zugang) — für die
   Sortierung „nach Einlagerung". Ohne Bewegung: leer. */
function letzteEinlagerung(db, weinId) {
  let neuster = "";
  for (const b of db.bewegungen || []) {
    if (b.weinId === weinId && b.art === "zugang" && (b.datum || "") > neuster) neuster = b.datum || "";
  }
  return neuster;
}


/* ================================================================
   Listenindex

   `letzteEinlagerung` und `besteBewertung` durchsuchen jeweils die
   VOLLE Bewegungs- bzw. Kritikliste. Je Zeile aufgerufen wird daraus
   ein Produkt: Bei 3600 Jahrgängen und 4800 Bewegungen sind das gut
   17 Millionen Vergleiche für eine einzige Liste — gemessen 93 ms
   allein für die Einlagerungen, dazu 26 ms für die Bewertungen.

   Der Index dreht das um: Einmal über jede Liste laufen, danach ist
   jede Zeile eine Nachschlagung. Aus O(Zeilen × Bewegungen) wird
   O(Zeilen + Bewegungen).

   Er wird je Neuberechnung einer Ansicht gebaut und lebt nicht
   länger — ein Zwischenspeicher, der über Änderungen hinweg gültig
   bleiben müsste, wäre eine zweite Quelle der Wahrheit neben `db`
   und würde früher oder später veralten.
   ================================================================ */
function baueListenIndex(db) {
  const einlagerung = new Map();          /* weinId → jüngstes Zugangsdatum */
  for (const b of db.bewegungen || []) {
    if (b.art !== "zugang") continue;
    const d = b.datum || "";
    if (d > (einlagerung.get(b.weinId) || "")) einlagerung.set(b.weinId, d);
  }

  /* Zwei Ebenen, weil beide gebraucht werden: die Weinliste zeigt die
     beste Bewertung des Weins, die Kellerliste die des Jahrgangs. */
  const bewertungWein = new Map();
  const bewertungJahrgang = new Map();
  const besser = (karte, schluessel, n) => {
    const alt = karte.get(schluessel);
    if (alt == null || n > alt) karte.set(schluessel, n);
  };
  for (const k of db.kritiken || []) {
    if (!k.weinId) continue;              /* offene Kritik, gehört keinem Wein */
    const n = normiert(k.schema, k.wert);
    if (n == null) continue;
    besser(bewertungWein, k.weinId, n);
    if (k.jgId) besser(bewertungJahrgang, `${k.weinId}|${k.jgId}`, n);
  }

  return {
    einlagerung: (weinId) => einlagerung.get(weinId) || "",
    bewertungWein: (weinId) => {
      const n = bewertungWein.get(weinId);
      return n === undefined ? null : n;
    },
    bewertungJahrgang: (weinId, jgId) => {
      const n = bewertungJahrgang.get(`${weinId}|${jgId}`);
      return n === undefined ? null : n;
    },
  };
}


/* Anzeigetext „Riesling 60 % · Spätburgunder 40 %" bzw. ohne Anteile
   „Riesling · Spätburgunder". */
function rebsortenText(wein) {
  const liste = rebsortenListe2(wein);
  if (liste.length === 0) return "";
  const mitAnteil = liste.length > 0 && liste.every((r) => r.anteil != null);
  return liste.map((r) => mitAnteil ? `${r.name} ${fmtZahl(r.anteil, 0)} %` : r.name).join(" · ");
}


/* Rebsorten nur als Namen, ohne Anteilsangaben — für Listen/Übersichten. */
function rebsortenNamen(wein) {
  return rebsortenListe2(wein).map((r) => r.name).join(" · ");
}


/* Beste (höchste) normierte Bewertung eines Weins bzw. Jahrgangs —
   macht Kritiken unterschiedlicher Schemata über min/max vergleichbar. */
function besteBewertung(db, weinId, jahr = null, jgId = null) {
  let best = null;
  for (const k of kritikenFuer(db, weinId, jahr, jgId)) {
    const n = normiert(k.schema, k.wert);
    if (n != null && (best == null || n > best)) best = n;
  }
  return best;
}


function macheVergleich(key) {
  const nameAsc = (a, b) => a.name.localeCompare(b.name, "de");
  /* leere Werte (kein Preis, keine Bewertung) stets ans Ende */
  const nl = (a, b, dir) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return dir * (a - b);
  };
  switch (key) {
    case "name-za":      return (a, b) => -nameAsc(a, b) || b.jahrMax - a.jahrMax;
    case "jahr-jung":    return (a, b) => b.jahrMax - a.jahrMax || nameAsc(a, b);
    case "jahr-alt":     return (a, b) => a.jahrMin - b.jahrMin || nameAsc(a, b);
    case "preis-ab":     return (a, b) => nl(a.preis, b.preis, -1) || nameAsc(a, b);
    case "preis-auf":    return (a, b) => nl(a.preis, b.preis, 1) || nameAsc(a, b);
    case "bewertung-ab": return (a, b) => nl(a.bewertung, b.bewertung, -1) || nameAsc(a, b);
    case "bewertung-auf":return (a, b) => nl(a.bewertung, b.bewertung, 1) || nameAsc(a, b);
    case "bestand-ab":   return (a, b) => (b.bestand || 0) - (a.bestand || 0) || nameAsc(a, b);
    case "reife-alt":    return (a, b) => nl(a.trinkab, b.trinkab, 1) || nameAsc(a, b);
    case "reife-jung":   return (a, b) => nl(a.trinkab, b.trinkab, -1) || nameAsc(a, b);
    case "reifeende-frueh": return (a, b) => nl(a.trinkbis, b.trinkbis, 1) || nameAsc(a, b);
    case "reifeende-spaet": return (a, b) => nl(a.trinkbis, b.trinkbis, -1) || nameAsc(a, b);
    case "einlag-neu":   return (a, b) => (b.einlagerung || "").localeCompare(a.einlagerung || "") || nameAsc(a, b);
    case "einlag-alt":   return (a, b) => (a.einlagerung || "").localeCompare(b.einlagerung || "") || nameAsc(a, b);
    default:             return (a, b) => nameAsc(a, b) || b.jahrMax - a.jahrMax;
  }
}


/* Schwartzian-Sort: projiziert einmal, sortiert, entpackt. */
function sortiere(liste, proj, key) {
  const cmp = macheVergleich(key);
  return liste
    .map((row) => ({ row, s: proj(row) }))
    .sort((a, b) => cmp(a.s, b.s))
    .map((x) => x.row);
}


/* Datenmigration: früherer Zähler `bestand` wird in die
   Größenaufteilung `bestaende` überführt (Standardgröße 0,75 l). */
/* Vor der Katalogeinführung vorhandene Stammdaten einmalig als lokal
   kennzeichnen — sonst landeten sie alle auf einmal beim Admin. */
function stempleBestand(db) {
  if (db.katalogStart) return db;
  const stemple = (liste) => (liste || []).map((o) => o.herkunft ? o : { ...o, herkunft: "lokal" });
  return {
    ...db,
    regions: stemple(db.regions),
    winzer: stemple(db.winzer),
    weine: stemple(db.weine),
  };
}

function migriere(dbRoh) {
  const db = stempleBestand(dbRoh);
  /* (weinId + jahr) → jahrgangId, um Alt-Verweise (Kritiken/Entnahmen/
     Bewegungen) auf die neue stabile Jahrgangs-ID umzuschlüsseln. */
  const jgIndex = new Map();
  const weine = db.weine.map((w) => {
    const rl = Array.isArray(w.rebsortenListe)
      ? w.rebsortenListe.map((r) => ({ name: String(r.name || "").trim(), anteil: r.anteil != null ? r.anteil : null })).filter((r) => r.name)
      : String(w.rebsorten || "").split(",").map((t) => t.trim()).filter(Boolean).map((name) => ({ name, anteil: null }));
    const anzeige = rl.length
      ? (rl.every((r) => r.anteil != null) ? rl.map((r) => `${r.name} ${Math.round(r.anteil)} %`) : rl.map((r) => r.name)).join(" · ")
      : "";
    return {
      ...w,
      rebsortenListe: rl,
      rebsorten: anzeige,
      jahrgaenge: w.jahrgaenge.map((j) => {
        let jg = j;
        if (!jg.id) jg = { ...jg, id: uid() };
        if (!jg.bestaende) {
          const { bestand, ...rest } = jg;
          jg = { ...rest, bestaende: { "0.75": bestand || 0 } };
        }
        if (jg.trinkVon === undefined && jg.trinkBis === undefined) {
          const treffer = String(jg.trinkfenster || "").match(/(\d{4})/g);
          const { trinkfenster, ...rest } = jg;
          jg = {
            ...rest,
            trinkVon: treffer && treffer[0] ? parseInt(treffer[0], 10) : null,
            trinkBis: treffer && treffer[1] ? parseInt(treffer[1], 10) : null,
          };
        }
        jgIndex.set(`${w.id}::${jg.jahr}`, jg.id);
        return jg;
      }),
    };
  });
  /* Alt-Verweise auf jgId heben (nur wo (weinId,jahr) eindeutig war). */
  const mitJgId = (liste) => (Array.isArray(liste) ? liste : []).map((x) =>
    x.jgId ? x : { ...x, jgId: jgIndex.get(`${x.weinId}::${x.jahr}`) || null });
  return {
    ...db,
    weatherCache: db.weatherCache || {},
    klimaCache: db.klimaCache || {},
    entnahmen: mitJgId(db.entnahmen),
    bewegungen: mitJgId(db.bewegungen),
    /* Kritiken bekommen `istEigene`. Alles, was vor dieser Fassung
       erfasst wurde, ist die Aufzeichnung des Nutzers selbst — auch
       wenn als Quelle ein fremder Kritiker steht. Der NAME bleibt
       deshalb unangetastet: „Jancis Robinson" ist eine Angabe, keine
       Vermutung, und sie zu überschreiben hieße, Information zu
       vernichten, die niemand wiederherstellen kann. */
    kritiken: mitJgId(db.kritiken).map((k) =>
      (typeof k.istEigene === "boolean" ? k : { ...k, istEigene: true })),
    prefs: { nurBestand: (db.prefs && typeof db.prefs.nurBestand === "boolean") ? db.prefs.nurBestand : true },
    /* Zeitpunkt der Katalogeinführung. Alles, was davor schon da war,
       gilt als lokal und wird NICHT nachträglich vorgeschlagen. */
    katalogStart: db.katalogStart || new Date().toISOString(),
    weinkarten: Array.isArray(db.weinkarten) ? db.weinkarten : [],
    weine,
  };
}


/* Jahrgang eines Weins über seine stabile ID (mit Rückfall auf jahr,
   solange Alt-Verweise noch ohne jgId auftreten). */
function jahrgangFuer(wein, jgId, jahrFallback) {
  if (!wein) return null;
  if (jgId) { const t = wein.jahrgaenge.find((j) => j.id === jgId); if (t) return t; }
  if (jahrFallback != null) return wein.jahrgaenge.find((j) => j.jahr === jahrFallback) || null;
  return null;
}


/* Reifegrad eines Jahrgangs anhand des Trinkfensters:
   "jung" (noch nicht trinkreif), "reif" (im Fenster), "ueber"
   (überfällig) oder null (kein Fenster erfasst). */
function reifeVonJahrgang(jg, jahr = new Date().getFullYear()) {
  const v = jg.trinkVon, b = jg.trinkBis;
  if (v == null && b == null) return null;
  if (v != null && jahr < v) return "jung";
  if (b != null && jahr > b) return "ueber";
  return "reif";
}


/* Reife eines Weins aus seinen Jahrgängen mit Bestand: bevorzugt
   trinkreif, sonst überfällig, sonst jung; für Filter/Sortierung. */
function reifeVonWein(wein, jahr = new Date().getFullYear()) {
  let hatReif = false, hatUeber = false, hatJung = false;
  for (const jg of wein.jahrgaenge) {
    if (gesamtFlaschen(jg) === 0) continue;
    const r = reifeVonJahrgang(jg, jahr);
    if (r === "reif") hatReif = true;
    else if (r === "ueber") hatUeber = true;
    else if (r === "jung") hatJung = true;
  }
  if (hatReif) return "reif";
  if (hatUeber) return "ueber";
  if (hatJung) return "jung";
  return null;
}


/* Frühester Trinkfensterbeginn eines Weins — für die Sortierung
   „nach Trinkreife". */
function trinkabVonWein(wein) {
  let min = null;
  for (const jg of wein.jahrgaenge) {
    if (jg.trinkVon != null && (min == null || jg.trinkVon < min)) min = jg.trinkVon;
  }
  return min;
}


/** Frühestes Ende des Trinkfensters eines Weins — „was wird als
    Nächstes überfällig?".

    Anders als bei `trinkabVonWein` zählen hier nur Jahrgänge MIT
    Bestand. Ein leergetrunkener Jahrgang kann nicht überfällig
    werden; ließe man ihn mitzählen, zöge er den Wein dauerhaft an den
    Anfang einer Liste, in der es um zu trinkende Flaschen geht. Hat
    der Wein gar keine Flaschen mehr, ist die Frage gegenstandslos —
    dann `null`, und die Zeile landet ans Ende.

    Der Unterschied ist gewollt: „ab wann darf ich" ist eine Frage an
    den Wein, „bis wann muss ich" eine an den Bestand. */
function trinkbisVonWein(wein) {
  let min = null;
  for (const jg of wein.jahrgaenge) {
    if (gesamtFlaschen(jg) === 0) continue;
    if (jg.trinkBis != null && (min == null || jg.trinkBis < min)) min = jg.trinkBis;
  }
  return min;
}


/* Reife eines Winzers: bevorzugt trinkreif über seine Weine hinweg. */
function reifeVonWinzer(db, winzerId, jahr = new Date().getFullYear()) {
  let hatReif = false, hatUeber = false, hatJung = false;
  for (const wein of db.weine) {
    if (wein.winzerId !== winzerId) continue;
    const r = reifeVonWein(wein, jahr);
    if (r === "reif") hatReif = true;
    else if (r === "ueber") hatUeber = true;
    else if (r === "jung") hatJung = true;
  }
  if (hatReif) return "reif";
  if (hatUeber) return "ueber";
  if (hatJung) return "jung";
  return null;
}


/* Trinkfenster als „2026–2032", „ab 2026" oder „—" darstellen. */
function trinkfensterText(jg) {
  const v = jg.trinkVon, b = jg.trinkBis;
  if (v != null && b != null) return `${v}–${b}`;
  if (v != null) return `ab ${v}`;
  if (b != null) return `bis ${b}`;
  return "—";
}


/* Sammelt eindeutige, nicht-leere Werte über alle Objekte —
   getrennte Werte (Kommas) werden aufgespalten (für Rebsorten). */
function eindeutigeWerte(objekte, feld, { split = false } = {}) {
  const menge = new Set();
  for (const o of objekte) {
    const roh = o ? o[feld] : null;
    if (roh == null) continue;
    const teile = split ? String(roh).split(/[,·]/) : [String(roh)];
    for (const t of teile) {
      const w = t.trim();
      if (w) menge.add(w);
    }
  }
  return [...menge].sort((a, b) => a.localeCompare(b, "de"));
}


/* ═══════════════════════════════════════════════════════════════
   Statistik: Kellerbestand und getrunkene Flaschen
   ═══════════════════════════════════════════════════════════════ */

/* Zählt Flaschen bzw. Volumen (in 0,75-l-Äquivalenten) je Kategorie.
   `zuweisung` liefert für einen Wein den Kategorienamen. */
function verteile(paare, zuweisung) {
  const map = new Map();
  for (const paar of paare) {
    const { flaschen, wert } = paar;
    const kats = zuweisung(paar);
    const liste = Array.isArray(kats) ? kats : [kats];
    for (const kat of liste) {
      const roh = typeof kat === "object" && kat ? kat.name : kat;
      const quote = typeof kat === "object" && kat && kat.quote != null ? kat.quote : 1;
      const name = roh && String(roh).trim() ? String(roh).trim() : "Ohne Angabe";
      const e = map.get(name) || { flaschen: 0, wert: 0 };
      e.flaschen += flaschen * quote;
      e.wert += wert * quote;
      map.set(name, e);
    }
  }
  return [...map.entries()]
    .map(([name, e]) => ({ name, flaschen: Math.round(e.flaschen * 10) / 10, wert: e.wert }))
    .sort((a, b) => b.wert - a.wert);
}


/* Durchschnittlicher Kaufpreis je Jahrgang aus den Zugängen —
   Grundlage für die Bewertung von Jahrgängen ohne eigenen Preis. */
function einstandsKarte(db) {
  const karte = new Map();
  for (const b of db.bewegungen || []) {
    if (b.art !== "zugang" || b.preis == null || !b.menge) continue;
    const e = karte.get(b.jgId) || { menge: 0, summe: 0 };
    e.menge += b.menge;
    e.summe += b.menge * b.preis;
    karte.set(b.jgId, e);
  }
  return karte;
}

/* Effektiver Wert einer Flasche dieses Jahrgangs: Der am Jahrgang
   erfasste Preis hat Vorrang; fehlt er, gilt der durchschnittliche
   Kaufpreis aus den Zugängen. Gegenstück zu bewegungsPreis, damit
   Kellerwert und Einstand dieselbe Lesart haben. */
function flaschenPreis(jg, karte) {
  if (jg && jg.preis != null) return jg.preis;
  const e = jg && karte ? karte.get(jg.id) : null;
  return e && e.menge > 0 ? e.summe / e.menge : null;
}


/* Wirkung einer Bewegung auf den Bestand: Zugang erhöht, Entnahme
   senkt. */
const bestandsWirkung = (b) => (b && b.art === "zugang" ? 1 : -1) * ((b && b.menge) || 0);

/* Bestandsänderungen beim Bearbeiten einer Bewegung, je Flaschengröße
   zusammengefasst: alte Wirkung zurücknehmen, neue anwenden.
   Zusammenfassen ist wesentlich — würde man beides nacheinander buchen
   und dabei jeweils auf 0 begrenzen, ginge der Abzug verloren, sobald
   die ursprüngliche Menge über dem heutigen Bestand liegt (weil seither
   entnommen wurde). Das Ergebnis wäre zu hoch. */
function bestandsDeltas(alt, neu) {
  const deltas = new Map();
  const zu = (g, d) => deltas.set(g, (deltas.get(g) || 0) + d);
  if (alt) zu(alt.groesse, -bestandsWirkung(alt));
  if (neu) zu(neu.groesse, bestandsWirkung(neu));
  return deltas;
}


/* Preis einer Bewegung je Flasche: Der protokollierte Preis hat
   Vorrang — fehlt er, gilt der am Jahrgang hinterlegte Preis. So
   zählen auch Zugänge ohne eigenen Kaufpreis in Einstand, Statistik
   und Verlauf mit. */
const bewegungsPreis = (jg, b) =>
  (b && b.preis != null) ? b.preis : (jg && jg.preis != null ? jg.preis : null);


/* Ein-/Auslagerungen je Kalenderjahr aus dem Bewegungslog —
   sowohl als Flaschenzahl als auch als Wert (€, über den Jahrgangspreis). */
function bewegungenNachJahr(db) {
  const weinById = new Map(db.weine.map((w) => [w.id, w]));
  const preisVon = (b) => {
    const w = weinById.get(b.weinId);
    const jg = jahrgangFuer(w, b.jgId, b.jahr);
    const p = bewegungsPreis(jg, b);
    return p != null ? p : 0;
  };
  const map = new Map();
  for (const b of db.bewegungen || []) {
    const jahr = b.datum ? new Date(b.datum).getFullYear() : null;
    if (!jahr) continue;
    const e = map.get(jahr) || { jahr, ein: 0, aus: 0, einWert: 0, ausWert: 0 };
    const wert = (b.menge || 0) * preisVon(b);
    if (b.art === "zugang") { e.ein += b.menge || 0; e.einWert += wert; }
    else if (b.art === "entnahme") { e.aus += b.menge || 0; e.ausWert += wert; }
    map.set(jahr, e);
  }
  return [...map.values()].sort((a, b) => a.jahr - b.jahr);
}


/* Aggregiert Bestand und Entnahmen zu allen Kennzahlen der Seite. */
function baueStatistik(db, jahr = null) {
  const weinById = new Map(db.weine.map((w) => [w.id, w]));

  const preisFuer = (wein, jgId, jahr) => {
    const jg = jahrgangFuer(wein, jgId, jahr);
    return jg && jg.preis != null ? jg.preis : null;
  };

  /* Jahresfilter: ohne Jahr zeigt „Bestand" den heutigen Keller und
     „Getrunken" alle Entnahmen. Mit Jahr werden stattdessen genau die
     Bewegungen dieses Kalenderjahres ausgewertet — eingelagert bzw.
     getrunken. Die Paare sind jahrgangsgenau, damit auch die
     Zuckerkategorie aus ihnen folgt. */
  const jahrVon = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d.getFullYear();
  };
  const sammle = (eintraege) => {
    const map = new Map();
    for (const { wein, jg, menge, preis } of eintraege) {
      if (!wein || !menge) continue;
      const k = wein.id + "|" + (jg ? jg.id : "-");
      const e = map.get(k) || { wein, jg: jg || null, flaschen: 0, wert: 0 };
      e.flaschen += menge;
      e.wert += menge * (preis != null ? preis : 0);
      map.set(k, e);
    }
    return [...map.values()];
  };
  const ausEntnahmen = (liste) => sammle(liste.map((e) => {
    const wein = weinById.get(e.weinId);
    if (!wein) return {};
    return {
      wein, jg: jahrgangFuer(wein, e.jgId, e.jahr), menge: e.menge,
      preis: e.preis != null ? e.preis : preisFuer(wein, e.jgId, e.jahr),
    };
  }));

  let bestandPaare;
  if (jahr == null) {
    /* Der erfasste Preis gilt je Flasche der jeweiligen Größe,
       daher Wert = Preis × Flaschenzahl (ohne Volumengewichtung). */
    bestandPaare = [];
    const einstand = einstandsKarte(db);
    for (const wein of db.weine) {
      for (const jg of wein.jahrgaenge) {
        const n = gesamtFlaschen(jg);
        if (n <= 0) continue;
        const p = flaschenPreis(jg, einstand);
        bestandPaare.push({ wein, jg, flaschen: n, wert: n * (p != null ? p : 0) });
      }
    }
  } else {
    bestandPaare = sammle((db.bewegungen || [])
      .filter((b) => b.art === "zugang" && jahrVon(b.datum) === jahr)
      .map((b) => {
        const wein = weinById.get(b.weinId);
        if (!wein) return {};
        return {
          wein, jg: jahrgangFuer(wein, b.jgId, b.jahr), menge: b.menge,
          preis: b.preis != null ? b.preis : preisFuer(wein, b.jgId, b.jahr),
        };
      }));
  }

  const trinkPaare = ausEntnahmen(
    (db.entnahmen || []).filter((e) => jahr == null || jahrVon(e.datum) === jahr),
  );

  const regionName = ({ wein }) => { const r = regionZuWein(db, wein); return r ? r.name : "Ohne Region"; };
  const landName = ({ wein }) => { const r = regionZuWein(db, wein); return r ? (r.land || "Ohne Land") : "Ohne Land"; };
  const winzerName = ({ wein }) => { const w = winzerZuWein(db, wein); return w ? w.name : "Ohne Winzer"; };
  const typName = ({ wein }) => (TYPEN[wein.typ] ? TYPEN[wein.typ].label : "Ohne Typ");
  const zuckerName = ({ jg }) => (jg && jg.zucker ? jg.zucker : "Ohne Angabe");
  /* Rebsorten: es wird ausschließlich nach der Sorte gruppiert, der
     erfasste Anteil bleibt unberücksichtigt. Jede Sorte eines Weins
     zählt damit voll; eine Cuvée erscheint in jeder ihrer Sorten. */
  const rebsorten = ({ wein }) => rebsortenListe2(wein).map((r) => r.name);

  const dimensionen = (paare) => ({
    typ: verteile(paare, typName),
    land: verteile(paare, landName),
    region: verteile(paare, regionName),
    rebsorte: verteile(paare, rebsorten),
    winzer: verteile(paare, winzerName),
    zucker: verteile(paare, zuckerName),
  });

  const summe = (paare) => paare.reduce((a, p) => ({ flaschen: a.flaschen + p.flaschen, wert: a.wert + p.wert }), { flaschen: 0, wert: 0 });
  const weineZahl = (paare) => new Set(paare.map((p) => p.wein.id)).size;

  return {
    bestand: { ...summe(bestandPaare), weine: weineZahl(bestandPaare), dim: dimensionen(bestandPaare) },
    getrunken: { ...summe(trinkPaare), weine: weineZahl(trinkPaare), dim: dimensionen(trinkPaare) },
    bewegungen: bewegungenNachJahr(db),
  };
}


/** Platzhalter wartender Etiketten gegen die echten Fotos tauschen.

    Wird ein Foto ohne Netz aufgenommen, steht im Jahrgang zunächst
    `pfad: "wartend:<id>"`. Sobald der Upload nachgeholt ist, muss der
    Verweis auf den Serverpfad zeigen — sonst zeigte das Kellerbuch auf
    ein Bild, das nur dieses eine Gerät kennt.

    `erledigt` ist die Liste aus `fotoWarteschlangeSenden()`:
    `[{ alt, foto }]`. Gibt das unveränderte Objekt zurück, wenn nichts
    zu tun war — der Speichereffekt springt dann nicht an. */
function ohneWartet(objekt) {
  const { wartet, ...rest } = objekt;
  return rest;
}

function ersetzeWartendeFotos(db, erledigt) {
  if (!db || !erledigt || erledigt.length === 0) return db;
  const nach = new Map(erledigt.map((e) => [e.alt, e.foto]));
  let getroffen = false;
  const weine = db.weine.map((w) => {
    let geaendert = false;
    const jahrgaenge = w.jahrgaenge.map((jg) => {
      const foto = jg.foto && nach.has(jg.foto.pfad);
      /* Das Rundum-Etikett geht denselben Weg; seine Angaben zu Form
         und Kapsel bleiben dabei stehen, nur die Bildpfade wechseln. */
      const rundum = jg.rundum && nach.has(jg.rundum.pfad);
      if (!foto && !rundum) return jg;
      geaendert = true; getroffen = true;
      return {
        ...jg,
        ...(foto ? { foto: nach.get(jg.foto.pfad) } : {}),
        ...(rundum ? { rundum: ohneWartet({ ...jg.rundum, ...nach.get(jg.rundum.pfad) }) } : {}),
      };
    });
    return geaendert ? { ...w, jahrgaenge } : w;
  });
  return getroffen ? { ...db, weine } : db;
}


export { ersetzeWartendeFotos, baueListenIndex, baueStatistik, besteBewertung, bestandsDeltas, bestandsWirkung, einstandsKarte, flaschenPreis, bewegungenNachJahr, bewegungsPreis, bewegungenVonWein, eindeutigeWerte, flaschenInRegion, flaschenVonWein, flaschenVonWinzer, gesamtFlaschen, hatRebsorte, jahrgaengeZahl, jahrgangFuer, jahrgangTitel, jahrgangsWert, kritikenFuer, letzteEinlagerung, macheVergleich, migriere, rebsortenListe, rebsortenListe2, rebsortenNamen, rebsortenText, regionZuWein, regionZuWinzer, reifeVonJahrgang, reifeVonWein, reifeVonWinzer, sortiere, trinkabVonWein, trinkbisVonWein, trinkfensterText, verteile, weineZahlVonWinzer, winzerZuWein };
